/**
 * InviteEmployeesMinimalModalV2 - Employee invitation modal with Tailwind/shadcn UI.
 * Replaces ChakraUi/Modals/InviteEmployeesMinimalModal.tsx
 */
import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Plus, Trash2, Loader2, AlertCircle } from 'lucide-react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import { CollectiveAgreementGroupI } from '../../api/companies/interfaces/CompanyInfo';
import {
	EmployeeInviteRequirements,
	RequiredEmployeeField
} from '../../api/companies/getEmployeeInviteRequirements';
import { InviteEmployeeMinimal } from '../../api/companies/inviteEmployeesMinimal';
import {
	validateEmployees,
	validateNif,
	validateDateFormat,
	validateMinAge,
	ValidationError
} from '../../utils/employeeInviteValidation';
import { formatSalaryInput } from '../../utils/salaryInputValidator';
import { isoToSpanish, spanishToIso } from '../../utils/dateUtils';
import { cn } from '../../lib/utils';

interface InviteEmployeesMinimalModalV2Props {
	isOpen: boolean;
	onClose: () => void;
	onInvite: (employees: InviteEmployeeMinimal[]) => void;
	requirements: EmployeeInviteRequirements | null;
	collectiveAgreementGroups: CollectiveAgreementGroupI[];
	isLoading?: boolean;
	isLoadingRequirements?: boolean;
	title?: string;
	companySelector?: React.ReactNode;
}

const emptyEmployee: InviteEmployeeMinimal = {
	nif: '',
	email: '',
	name: '',
	firstSurname: '',
	lastSurname: '',
	birthdate: '',
	gender: 'M'
};

const InviteEmployeesMinimalModalV2: React.FC<InviteEmployeesMinimalModalV2Props> = ({
	isOpen,
	onClose,
	onInvite,
	requirements,
	collectiveAgreementGroups,
	isLoading = false,
	isLoadingRequirements = false,
	title,
	companySelector
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);

	const [employees, setEmployees] = useState<InviteEmployeeMinimal[]>([{ ...emptyEmployee }]);
	const [employeeErrors, setEmployeeErrors] = useState<ValidationError[][]>([]);
	const [salaryInputValues, setSalaryInputValues] = useState<Record<number, string>>({});
	const [submitError, setSubmitError] = useState<string | null>(null);

	// Reset form when modal opens
	useEffect(() => {
		if (isOpen) {
			setEmployees([{ ...emptyEmployee }]);
			setEmployeeErrors([]);
			setSalaryInputValues({});
			setSubmitError(null);
		}
	}, [isOpen, requirements]);

	const hasRequiredField = useCallback(
		(field: RequiredEmployeeField): boolean => {
			return requirements?.requiredFields.includes(field) ?? false;
		},
		[requirements]
	);

	const handleAddEmployee = () => {
		setEmployees([...employees, { ...emptyEmployee }]);
		setEmployeeErrors([...employeeErrors, []]);
	};

	const handleRemoveEmployee = (index: number) => {
		if (employees.length > 1) {
			setEmployees(employees.filter((_, i) => i !== index));
			setEmployeeErrors(employeeErrors.filter((_, i) => i !== index));
			const newSalaryInputs = { ...salaryInputValues };
			delete newSalaryInputs[index];
			setSalaryInputValues(newSalaryInputs);
		}
	};

	const updateEmployee = (
		index: number,
		field: keyof InviteEmployeeMinimal,
		value: string | number
	) => {
		const updated = [...employees];
		updated[index] = { ...updated[index], [field]: value };
		setEmployees(updated);

		if (employeeErrors[index]) {
			const updatedErrors = [...employeeErrors];
			updatedErrors[index] = updatedErrors[index].filter((e) => e.field !== field);
			setEmployeeErrors(updatedErrors);
		}
	};

	const handleSalaryChange = (index: number, value: string) => {
		const formatted = formatSalaryInput(value);
		setSalaryInputValues({ ...salaryInputValues, [index]: formatted });
		const parsed = parseFloat(formatted.replace(',', '.')) || 0;
		updateEmployee(index, 'grossAnnualSalary', parsed);
	};

	const validateNifField = (index: number, value: string) => {
		const updatedErrors = [...employeeErrors];
		if (!updatedErrors[index]) {
			updatedErrors[index] = [];
		}
		updatedErrors[index] = updatedErrors[index].filter((e) => e.field !== 'nif');

		if (!value.trim()) {
			updatedErrors[index].push({ field: 'nif', message: 'NIF requerido' });
		} else if (!validateNif(value)) {
			updatedErrors[index].push({ field: 'nif', message: 'NIF/NIE inválido' });
		}
		setEmployeeErrors(updatedErrors);
	};

	const validateBirthdateField = (index: number, spanishDate: string) => {
		const updatedErrors = [...employeeErrors];
		if (!updatedErrors[index]) {
			updatedErrors[index] = [];
		}
		updatedErrors[index] = updatedErrors[index].filter((e) => e.field !== 'birthdate');

		if (spanishDate && validateDateFormat(spanishDate) && !validateMinAge(spanishDate, 18)) {
			updatedErrors[index].push({
				field: 'birthdate',
				message: 'El empleado debe tener al menos 18 años'
			});
		}
		setEmployeeErrors(updatedErrors);
	};

	const validateAllEmployees = (): boolean => {
		if (!requirements) return false;
		const result = validateEmployees(employees, requirements);
		setEmployeeErrors(result.employeeErrors.map((e) => e.errors));
		return result.invalidCount === 0;
	};

	const handleInvite = () => {
		if (!requirements) return;
		// Validation must surface feedback: a silent `return` (the V2 regression)
		// left the user with a button that "does nothing" — no toast, no backend call.
		if (!validateAllEmployees()) {
			setSubmitError(translate('admin.staff.inviteFixErrorsBeforeSubmit'));
			return;
		}
		const validEmployees = employees.filter((emp) => emp.email.trim() !== '');
		if (validEmployees.length === 0) {
			setSubmitError(translate('admin.staff.inviteAddAtLeastOneEmployee'));
			return;
		}
		setSubmitError(null);
		onInvite(validEmployees);
	};

	const handleClose = () => {
		setEmployees([{ ...emptyEmployee }]);
		setEmployeeErrors([]);
		setSalaryInputValues({});
		setSubmitError(null);
		onClose();
	};

	const getFieldError = (employeeIndex: number, field: string): string | undefined => {
		const errors = employeeErrors[employeeIndex];
		if (!errors) return undefined;
		const error = errors.find((e) => e.field === field);
		return error?.message;
	};

	const validEmployeesCount = employees.filter(
		(emp) => emp.email.trim() !== '' && emp.name.trim() !== '' && emp.nif.trim() !== ''
	).length;

	const isValid = validEmployeesCount > 0;

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col">
				<DialogHeader>
					<DialogTitle>{title || translate('admin.staff.inviteEmployees')}</DialogTitle>
					<DialogDescription>{translate('admin.staff.inviteDescription')}</DialogDescription>
				</DialogHeader>

				{companySelector && <div className="mb-4">{companySelector}</div>}

				{submitError && (
					<div
						role="alert"
						className="mb-4 flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
					>
						<AlertCircle className="w-4 h-4 shrink-0" />
						<span>{submitError}</span>
					</div>
				)}

				{isLoadingRequirements ? (
					<div className="flex items-center justify-center py-12">
						<Loader2 className="w-8 h-8 animate-spin text-primary" />
					</div>
				) : (
					<ScrollArea className="flex-1 pr-4 -mr-4">
						<div className="space-y-4">
							{employees.map((employee, index) => (
								<div key={index} className="rounded-lg border border-border p-4 bg-card relative">
									{employees.length > 1 && (
										<button
											onClick={() => handleRemoveEmployee(index)}
											className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-destructive transition-colors"
										>
											<Trash2 className="w-4 h-4" />
										</button>
									)}

									<p className="text-sm font-medium text-muted-foreground mb-3">
										{translate('admin.staff.employee')} {index + 1}
									</p>

									{/* Row 1: NIF, Email */}
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
										<div className="space-y-1.5">
											<Label className="text-sm">NIF/NIE *</Label>
											<Input
												placeholder="12345678A"
												value={employee.nif}
												onChange={(e) => updateEmployee(index, 'nif', e.target.value.toUpperCase())}
												onBlur={(e) => validateNifField(index, e.target.value)}
												className={cn(getFieldError(index, 'nif') && 'border-destructive')}
											/>
											{getFieldError(index, 'nif') && (
												<p className="text-xs text-destructive flex items-center gap-1">
													<AlertCircle className="w-3 h-3" />
													{getFieldError(index, 'nif')}
												</p>
											)}
										</div>

										<div className="space-y-1.5">
											<Label className="text-sm">{translate('admin.staff.email')} *</Label>
											<Input
												type="email"
												placeholder="empleado@empresa.com"
												value={employee.email}
												onChange={(e) => updateEmployee(index, 'email', e.target.value)}
												className={cn(getFieldError(index, 'email') && 'border-destructive')}
											/>
											{getFieldError(index, 'email') && (
												<p className="text-xs text-destructive flex items-center gap-1">
													<AlertCircle className="w-3 h-3" />
													{getFieldError(index, 'email')}
												</p>
											)}
										</div>
									</div>

									{/* Row 2: Name, First Surname, Last Surname */}
									<div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
										<div className="space-y-1.5">
											<Label className="text-sm">{translate('admin.staff.name')} *</Label>
											<Input
												placeholder="Juan"
												value={employee.name}
												onChange={(e) => updateEmployee(index, 'name', e.target.value)}
												className={cn(getFieldError(index, 'name') && 'border-destructive')}
											/>
											{getFieldError(index, 'name') && (
												<p className="text-xs text-destructive">{getFieldError(index, 'name')}</p>
											)}
										</div>

										<div className="space-y-1.5">
											<Label className="text-sm">{translate('admin.staff.firstSurname')} *</Label>
											<Input
												placeholder="García"
												value={employee.firstSurname}
												onChange={(e) => updateEmployee(index, 'firstSurname', e.target.value)}
												className={cn(getFieldError(index, 'firstSurname') && 'border-destructive')}
											/>
										</div>

										<div className="space-y-1.5">
											<Label className="text-sm">{translate('admin.staff.lastSurname')}</Label>
											<Input
												placeholder="López"
												value={employee.lastSurname || ''}
												onChange={(e) => updateEmployee(index, 'lastSurname', e.target.value)}
											/>
										</div>
									</div>

									{/* Row 3: Birthdate, Gender */}
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
										<div className="space-y-1.5">
											<Label className="text-sm">{translate('admin.staff.birthdate')} *</Label>
											<Input
												type="date"
												value={spanishToIso(employee.birthdate)}
												onChange={(e) =>
													updateEmployee(index, 'birthdate', isoToSpanish(e.target.value))
												}
												onBlur={() => validateBirthdateField(index, employee.birthdate)}
												max={new Date().toISOString().split('T')[0]}
												className={cn(getFieldError(index, 'birthdate') && 'border-destructive')}
											/>
											{getFieldError(index, 'birthdate') && (
												<p className="text-xs text-destructive flex items-center gap-1">
													<AlertCircle className="w-3 h-3" />
													{getFieldError(index, 'birthdate')}
												</p>
											)}
										</div>

										<div className="space-y-1.5">
											<Label className="text-sm">{translate('admin.staff.gender')} *</Label>
											<Select
												value={employee.gender}
												onValueChange={(value) => updateEmployee(index, 'gender', value)}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="M">{translate('admin.staff.male')}</SelectItem>
													<SelectItem value="F">{translate('admin.staff.female')}</SelectItem>
												</SelectContent>
											</Select>
										</div>
									</div>

									{/* Conditional Fields */}
									{(hasRequiredField(RequiredEmployeeField.GROSS_ANNUAL_SALARY) ||
										hasRequiredField(RequiredEmployeeField.EMPLOYMENT_START_DATE) ||
										hasRequiredField(RequiredEmployeeField.CATEGORY) ||
										hasRequiredField(RequiredEmployeeField.COLLECTIVE_AGREEMENT_GROUP)) && (
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
											{hasRequiredField(RequiredEmployeeField.GROSS_ANNUAL_SALARY) && (
												<div className="space-y-1.5">
													<Label className="text-sm">
														{translate('admin.staff.grossAnnualSalary')} *
													</Label>
													<div className="relative">
														<Input
															placeholder="35000,00"
															value={salaryInputValues[index] || ''}
															onChange={(e) => handleSalaryChange(index, e.target.value)}
															inputMode="decimal"
															className={cn(
																getFieldError(index, 'grossAnnualSalary') && 'border-destructive',
																'pr-8'
															)}
														/>
														<span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
															€
														</span>
													</div>
												</div>
											)}

											{hasRequiredField(RequiredEmployeeField.EMPLOYMENT_START_DATE) && (
												<div className="space-y-1.5">
													<Label className="text-sm">
														{translate('admin.staff.employmentStartDate')} *
													</Label>
													<Input
														type="date"
														value={spanishToIso(employee.employmentStartDate || '')}
														onChange={(e) =>
															updateEmployee(
																index,
																'employmentStartDate',
																isoToSpanish(e.target.value)
															)
														}
													/>
												</div>
											)}

											{hasRequiredField(RequiredEmployeeField.CATEGORY) && (
												<div className="space-y-1.5">
													<Label className="text-sm">{translate('admin.staff.category')} *</Label>
													<Select
														value={employee.category || ''}
														onValueChange={(value) => updateEmployee(index, 'category', value)}
													>
														<SelectTrigger>
															<SelectValue placeholder={translate('admin.staff.selectCategory')} />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="DIRECTOR">
																{translate('admin.staff.categoryDirector')}
															</SelectItem>
															<SelectItem value="TECHNICAL_DIRECTOR">
																{translate('admin.staff.categoryTechnicalDirector')}
															</SelectItem>
															<SelectItem value="NON_DIRECTOR">
																{translate('admin.staff.categoryNonDirector')}
															</SelectItem>
														</SelectContent>
													</Select>
												</div>
											)}

											{hasRequiredField(RequiredEmployeeField.COLLECTIVE_AGREEMENT_GROUP) && (
												<div className="space-y-1.5">
													<Label className="text-sm">
														{translate('admin.staff.collectiveAgreementGroup')} *
													</Label>
													{/* The field is required by the backend whenever the company has a
													    collective agreement. It must ALWAYS render when required —
													    hiding it on an empty group list (the V2 bug) produced a
													    hidden-but-required field that silently blocked submission. */}
													{collectiveAgreementGroups.length > 0 ? (
														<Select
															value={employee.collectiveAgreementGroup || ''}
															onValueChange={(value) =>
																updateEmployee(index, 'collectiveAgreementGroup', value)
															}
														>
															<SelectTrigger>
																<SelectValue placeholder={translate('admin.staff.selectGroup')} />
															</SelectTrigger>
															<SelectContent>
																{collectiveAgreementGroups.map((group) => (
																	<SelectItem key={group.id} value={group.id}>
																		{group.label}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
													) : (
														<p className="text-xs text-destructive flex items-center gap-1">
															<AlertCircle className="w-3 h-3" />
															{translate('admin.staff.collectiveAgreementGroupsUnavailable')}
														</p>
													)}
												</div>
											)}
										</div>
									)}
								</div>
							))}

							{/* Add Employee Button */}
							<div className="flex items-center justify-between">
								<Button variant="outline" size="sm" onClick={handleAddEmployee} className="gap-2">
									<Plus className="w-4 h-4" />
									{translate('admin.staff.addEmployee')}
								</Button>

								<Badge variant="secondary" className="rounded-full">
									{translate('admin.staff.totalEmployees')}: {validEmployeesCount}
								</Badge>
							</div>
						</div>
					</ScrollArea>
				)}

				<DialogFooter className="gap-2 pt-4 border-t">
					<Button variant="outline" onClick={handleClose}>
						{translate('general.genericModal.close')}
					</Button>
					<Button
						onClick={handleInvite}
						disabled={!isValid || isLoading || !requirements}
						className="bg-primary hover:bg-primary/90"
					>
						{isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
						{translate('admin.staff.sendInvitations')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default InviteEmployeesMinimalModalV2;
